class ProductSectionController {
  constructor(section) {
    this.section = section;
    this.product = this.parseProduct();
    this.form = section.querySelector('[data-product-form]');
    this.gallery = section.querySelector('[data-product-gallery]');
    this.viewport = section.querySelector('[data-gallery-viewport]');
    this.stickyBar = section.querySelector('[data-sticky-bar]');
    this.optionButtons = [...section.querySelectorAll('[data-option-button]')];
    this.optionSelects = [...section.querySelectorAll('[data-option-select]')];
    this.variantIdInputs = [...section.querySelectorAll('[data-variant-id-input]')];
    this.quantityInput = section.querySelector('[data-quantity-input]');
    this.accordionTriggers = [...section.querySelectorAll('[data-accordion-trigger]')];
    this.price = section.querySelector('[data-product-price]');
    this.comparePrice = section.querySelector('[data-product-compare-price]');
    this.savingsBadge = section.querySelector('[data-product-savings-badge]');
    this.priceWrap = section.querySelector('[data-savings-format]');
    this.savingsFormat = this.priceWrap?.dataset.savingsFormat || 'percent';
    this.savingsTemplatePercent = this.priceWrap?.dataset.savingsTemplatePercent || 'Save [[value]] today';
    this.savingsTemplateAmount = this.priceWrap?.dataset.savingsTemplateAmount || 'Save [[value]] today';
    this.inventory = section.querySelector('[data-product-inventory]');
    this.sku = section.querySelector('[data-product-sku]');
    this.stickyPrice = section.querySelector('[data-sticky-price]');
    this.addToCart = section.querySelector('[data-add-to-cart]');
    this.addToCartLabel = section.querySelector('[data-add-to-cart-label]');
    this.stickySubmit = section.querySelector('[data-sticky-submit]');
    this.addToCartText = section.dataset.addToCartLabel || 'Add to cart';
    this.soldOutText = section.dataset.soldOutLabel || 'Sold out';
    this.unavailableText = section.dataset.unavailableLabel || 'Unavailable';
    this.availableToOrderText = section.dataset.availableToOrderLabel || 'Available to order';
    this.inStockTemplate = section.dataset.inStockTemplate || '[[count]] in stock';
    this.skuTemplate = section.dataset.skuTemplate || 'SKU: [[value]]';
    this.currentVariant = this.findInitialVariant();

    if (!this.product || !this.form) return;

    this.bindOptionInputs();
    this.bindQuantityControls();
    this.bindGallery();
    this.setupPeekLoop();
    this.bindStickyBar();
    this.bindAccordions();
    this.applySelections(this.currentVariant?.options || this.getSelectedOptions());
    this.updateVariantUI(this.currentVariant);
  }

  parseProduct() {
    const node = this.section.querySelector('[data-product-json]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('Unable to parse product JSON', error);
      return null;
    }
  }

  findInitialVariant() {
    const currentId = Number(this.form.querySelector('[name="id"]')?.value);

    return (
      this.product.variants.find((variant) => variant.id === currentId) ||
      this.product.selected_or_first_available_variant ||
      this.product.variants[0]
    );
  }

  bindOptionInputs() {
    this.optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;

        const optionName = button.dataset.optionName;
        const optionValue = button.dataset.optionValue;

        this.handleVariantChange(optionName, optionValue);
      });
    });

    this.optionSelects.forEach((select) => {
      select.addEventListener('change', () => {
        this.handleVariantChange(
          select.dataset.optionName,
          select.value
        );
      });
    });
  }

  updateSelectedValue(optionName, optionValue) {
    const option = this.product.options.findIndex(
      (name) => name === optionName
    );

    const label = this.section.querySelector(
      `[data-selected-option="${option + 1}"]`
    );

    if (label) {
      label.textContent = optionValue;
    }
  }

  getSelectedOptions() {
    return this.product.options.map((optionName) => {
      const select = this.optionSelects.find(
        (item) => item.dataset.optionName === optionName
      );

      if (select) {
        return select.value;
      }

      const button = this.optionButtons.find(
        (item) =>
          item.dataset.optionName === optionName &&
          item.classList.contains('is-active')
      );

      return button?.dataset.optionValue;
    });
  }

  handleVariantChange(optionName, optionValue) {
    const optionIndex = this.product.options.findIndex(
      (name) => name === optionName
    );

    const selectedOptions = this.getSelectedOptions();

    if (optionIndex >= 0) {
      selectedOptions[optionIndex] = optionValue;
    }

    const nextVariant = this.resolveVariantFromSelection(
      selectedOptions,
      optionIndex
    );

    if (!nextVariant) return;

    const normalizedSelections = [...nextVariant.options];

    this.applySelections(normalizedSelections);

    this.currentVariant = nextVariant;

    this.updateVariantUI(nextVariant);
  }

  applySelections(selectedOptions) {
    this.product.options.forEach((optionName, index) => {
      const selectedValue = selectedOptions[index];

      const relatedButtons = this.optionButtons.filter(
        (button) => button.dataset.optionName === optionName
      );

      relatedButtons.forEach((button) => {
        const isActive =
          button.dataset.optionValue === selectedValue;

        button.classList.toggle('is-active', isActive);

        button.setAttribute(
          'aria-pressed',
          isActive ? 'true' : 'false'
        );
      });

      const select = this.optionSelects.find(
        (item) => item.dataset.optionName === optionName
      );

      if (select) {
        select.value = selectedValue;
      }

      this.updateSelectedValue(
        optionName,
        selectedValue
      );
    });

    this.updateOptionAvailability(selectedOptions);
  }

  updateOptionAvailability(selectedOptions) {
    this.product.options.forEach((optionName, index) => {
      this.optionButtons
        .filter(
          (button) => button.dataset.optionName === optionName
        )
        .forEach((button) => {
          const isReachable = this.isOptionValueReachable(
            index,
            button.dataset.optionValue,
            selectedOptions
          );

          button.disabled = !isReachable;

          button.classList.toggle(
            'is-unavailable',
            !isReachable
          );

          button.setAttribute(
            'aria-disabled',
            isReachable ? 'false' : 'true'
          );

          if (!isReachable) {
            button.title =
              button.dataset.unavailableLabel ||
              'Sold out';
          } else {
            button.removeAttribute('title');
          }
        });

      const select = this.optionSelects.find(
        (item) => item.dataset.optionName === optionName
      );

      if (!select) return;

      [...select.options].forEach((option) => {
        option.disabled =
          !this.isOptionValueReachable(
            index,
            option.value,
            selectedOptions
          );
      });
    });
  }

  isOptionValueReachable(
    index,
    optionValue,
    selectedOptions
  ) {
    return this.product.variants.some((variant) =>
      variant.options.every((value, variantIndex) => {
        if (variantIndex === index) {
          return value === optionValue;
        }

        const selectedValue =
          selectedOptions[variantIndex];

        return !selectedValue || value === selectedValue;
      })
    );
  }

  resolveVariantFromSelection(
    selectedOptions,
    changedIndex = 0
  ) {
    const exactVariant =
      this.findVariantByOptions(selectedOptions);

    if (exactVariant) {
      return exactVariant;
    }

    const rankedVariants = this.product.variants
      .filter((variant) => {
        if (changedIndex < 0) return true;

        return (
          variant.options[changedIndex] ===
          selectedOptions[changedIndex]
        );
      })
      .map((variant) => ({
        variant,

        score: variant.options.reduce(
          (total, value, index) =>
            total +
            (value === selectedOptions[index] ? 1 : 0),
          0
        )
      }))
      .sort((left, right) => {
        if (right.score !== left.score) {
          return right.score - left.score;
        }

        if (
          left.variant.available !==
          right.variant.available
        ) {
          return (
            Number(right.variant.available) -
            Number(left.variant.available)
          );
        }

        return 0;
      });

    return rankedVariants[0]?.variant || null;
  }

  findVariantByOptions(selectedOptions) {
    return this.product.variants.find((variant) =>
      variant.options.every(
        (option, index) =>
          option === selectedOptions[index]
      )
    );
  }

  updateVariantUI(variant) {
    this.variantIdInputs.forEach((input) => {
      input.value = variant.id;
    });

    if (this.price) {
      this.price.textContent =
        this.formatMoney(variant.price);
    }

    if (this.stickyPrice) {
      this.stickyPrice.textContent =
        this.formatMoney(variant.price);
    }

    if (this.comparePrice) {
      if (
        variant.compare_at_price >
        variant.price
      ) {
        this.comparePrice.hidden = false;

        this.comparePrice.textContent =
          this.formatMoney(
            variant.compare_at_price
          );
      } else {
        this.comparePrice.hidden = true;
        this.comparePrice.textContent = '';
      }
    }

    if (this.savingsBadge) {
      if (
        variant.compare_at_price >
        variant.price
      ) {
        const savingsAmount =
          variant.compare_at_price -
          variant.price;

        const savingsPercent =
          Math.round(
            (savingsAmount /
              variant.compare_at_price) *
              100
          );

        this.savingsBadge.hidden = false;

        this.savingsBadge.textContent =
          this.savingsFormat === 'amount'
            ? this.savingsTemplateAmount.replace(
                '[[value]]',
                this.formatMoney(
                  savingsAmount
                )
              )
            : this.savingsTemplatePercent.replace(
                '[[value]]',
                `${savingsPercent}%`
              );
      } else {
        this.savingsBadge.hidden = true;
        this.savingsBadge.textContent = '';
      }
    }

    if (this.inventory) {
      this.inventory.textContent =
        variant.available
          ? variant.inventory_quantity > 0
            ? this.inStockTemplate.replace(
                '[[count]]',
                variant.inventory_quantity
              )
            : this.availableToOrderText
          : this.unavailableText;
    }

    if (this.sku) {
      this.sku.textContent =
        variant.sku
          ? this.skuTemplate.replace(
              '[[value]]',
              variant.sku
            )
          : '';
    }

    if (
      this.addToCart &&
      this.addToCartLabel
    ) {
      this.addToCart.disabled =
        !variant.available;

      this.addToCartLabel.textContent =
        variant.available
          ? this.addToCartText
          : this.soldOutText;
    }

    if (variant.featured_media?.id) {
      this.showMedia(
        variant.featured_media.id
      );
    }

    this.syncStickyFromMainVariant(variant);

    const nextUrl =
      `${this.section.dataset.productUrl}?variant=${variant.id}`;

    window.history.replaceState(
      {
        variantId: variant.id
      },
      '',
      nextUrl
    );
  }

  bindGallery() {
    if (
      !this.gallery ||
      !this.viewport
    ) {
      return;
    }

    const thumbButtons = [
      ...this.section.querySelectorAll(
        '[data-gallery-thumb]'
      )
    ];

    const items = [
      ...this.section.querySelectorAll(
        '[data-media-id]'
      )
    ];

    const prev =
      this.section.querySelector(
        '[data-gallery-prev]'
      );

    const next =
      this.section.querySelector(
        '[data-gallery-next]'
      );

    const zoomButtons = [
      ...this.section.querySelectorAll(
        '[data-gallery-zoom]'
      )
    ];

    const modal =
      this.section.querySelector(
        '[data-gallery-modal]'
      );

    const modalBody =
      this.section.querySelector(
        '[data-gallery-modal-body]'
      );

    const modalThumbs =
      this.section.querySelector(
        '[data-gallery-modal-thumbs]'
      );

    const modalTemplates = [
      ...this.section.querySelectorAll(
        '[data-gallery-modal-template]'
      )
    ];

    const closeModal =
      this.section.querySelector(
        '[data-gallery-close]'
      );

    const mediaIds = items.map((item) =>
      Number(item.dataset.mediaId)
    );

    let lastZoomTrigger = null;
    let activeModalMediaId = null;

    thumbButtons.forEach((button) => {
      button.addEventListener(
        'click',
        () =>
          this.showMedia(
            Number(
              button.dataset.targetMediaId
            )
          )
      );
    });

    const scrollToOffset = (direction) => {
      this.viewport.scrollBy({
        left:
          direction *
          this.viewport.clientWidth,

        behavior: 'smooth'
      });
    };

    prev?.addEventListener(
      'click',
      () => scrollToOffset(-1)
    );

    next?.addEventListener(
      'click',
      () => scrollToOffset(1)
    );

    const renderModalMedia = (mediaId) => {
      const template =
        modalTemplates.find(
          (item) =>
            Number(
              item.dataset.galleryModalTemplate
            ) === mediaId
        );

      if (
        !template ||
        !modalBody
      ) {
        return;
      }

      modalBody.replaceChildren(
        template.content.cloneNode(true)
      );

      activeModalMediaId = mediaId;

      modalThumbs
        ?.querySelectorAll(
          '[data-gallery-modal-thumb]'
        )
        .forEach((thumb) => {
          const isActive =
            Number(
              thumb.dataset.targetMediaId
            ) === mediaId;

          thumb.classList.toggle(
            'is-active',
            isActive
          );

          thumb.setAttribute(
            'aria-current',
            isActive ? 'true' : 'false'
          );
        });
    };

    const renderAdjacentModalMedia =
      (direction) => {
        if (
          !mediaIds.length ||
          activeModalMediaId === null
        ) {
          return;
        }

        const currentIndex =
          mediaIds.indexOf(
            activeModalMediaId
          );

        if (currentIndex < 0) {
          return;
        }

        const nextIndex =
          (
            currentIndex +
            direction +
            mediaIds.length
          ) %
          mediaIds.length;

        renderModalMedia(
          mediaIds[nextIndex]
        );
      };

    modalThumbs
      ?.querySelectorAll(
        '[data-gallery-modal-thumb]'
      )
      .forEach((thumb) => {
        thumb.addEventListener(
          'click',
          () => {
            renderModalMedia(
              Number(
                thumb.dataset.targetMediaId
              )
            );
          }
        );
      });

    zoomButtons.forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const item =
            button.closest(
              '[data-media-id]'
            );

          if (
            !item ||
            !modal ||
            !modalBody
          ) {
            return;
          }

          lastZoomTrigger = button;

          renderModalMedia(
            Number(item.dataset.mediaId)
          );

          document
            .documentElement
            .classList.add(
              'product-gallery-modal-open'
            );

          document.body.classList.add(
            'product-gallery-modal-open'
          );

          modal.showModal();

          closeModal?.focus();
        }
      );
    });

    closeModal?.addEventListener(
      'click',
      () => modal?.close()
    );

    modal?.addEventListener(
      'click',
      (event) => {
        if (event.target === modal) {
          modal.close();
        }
      }
    );

    modal?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();

          renderAdjacentModalMedia(-1);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();

          renderAdjacentModalMedia(1);
        }
      }
    );

    modal?.addEventListener(
      'close',
      () => {
        modalBody?.replaceChildren();

        activeModalMediaId = null;

        document
          .documentElement
          .classList.remove(
            'product-gallery-modal-open'
          );

        document.body.classList.remove(
          'product-gallery-modal-open'
        );

        lastZoomTrigger?.focus();
      }
    );

    const observer =
      new IntersectionObserver(
        (entries) => {
          const visible =
            entries.find(
              (entry) =>
                entry.isIntersecting
            );

          if (!visible) {
            return;
          }

          const mediaId =
            Number(
              visible.target.dataset.mediaId
            );

          items.forEach((item) =>
            item.classList.toggle(
              'is-active',
              item === visible.target
            )
          );

          thumbButtons.forEach(
            (button) =>
              button.classList.toggle(
                'is-active',
                Number(
                  button.dataset.targetMediaId
                ) === mediaId
              )
          );
        },
        {
          root: this.viewport,
          threshold: 0.55
        }
      );

    items.forEach((item) =>
      observer.observe(item)
    );
  }

  setupPeekLoop() {
    const viewport = this.viewport;

    if (
      !viewport ||
      !this.gallery ||
      !this.gallery.classList.contains(
        'product-gallery--mobile-peek'
      )
    ) {
      return;
    }

    const reals = [
      ...viewport.children
    ].filter((child) =>
      child.hasAttribute(
        'data-media-id'
      )
    );

    if (reals.length < 2) {
      return;
    }

    const mq =
      window.matchMedia(
        '(max-width: 989px)'
      );

    let clones = [];
    let scrollTimer = null;
    let jumping = false;

    const centerOn = (el) => {
      jumping = true;

      const itemLeft =
        el.getBoundingClientRect().left -
        viewport.getBoundingClientRect().left +
        viewport.scrollLeft;

      viewport.scrollTo({
        left:
          itemLeft -
          (
            viewport.clientWidth -
            el.offsetWidth
          ) /
            2,

        behavior: 'auto'
      });

      window.requestAnimationFrame(
        () => {
          jumping = false;
        }
      );
    };

    const isCentered = (el) => {
      const vpRect =
        viewport.getBoundingClientRect();

      const rect =
        el.getBoundingClientRect();

      return (
        Math.abs(
          rect.left +
            rect.width / 2 -
            (
              vpRect.left +
              vpRect.width / 2
            )
        ) <
        rect.width / 2
      );
    };

    const handleSettle = () => {
      if (
        jumping ||
        clones.length !== 2
      ) {
        return;
      }

      if (isCentered(clones[0])) {
        centerOn(
          reals[reals.length - 1]
        );
      } else if (
        isCentered(clones[1])
      ) {
        centerOn(reals[0]);
      }
    };

    const onScroll = () => {
      window.clearTimeout(
        scrollTimer
      );

      scrollTimer =
        window.setTimeout(
          handleSettle,
          130
        );
    };

    const enable = () => {
      if (clones.length) {
        return;
      }

      const leadClone =
        reals[
          reals.length - 1
        ].cloneNode(true);

      const tailClone =
        reals[0].cloneNode(true);

      [leadClone, tailClone].forEach(
        (clone) => {
          clone.removeAttribute(
            'data-media-id'
          );

          clone.setAttribute(
            'aria-hidden',
            'true'
          );

          clone
            .querySelectorAll('[id]')
            .forEach((node) =>
              node.removeAttribute('id')
            );
        }
      );

      viewport.insertBefore(
        leadClone,
        reals[0]
      );

      viewport.appendChild(
        tailClone
      );

      clones = [
        leadClone,
        tailClone
      ];

      viewport.addEventListener(
        'scroll',
        onScroll,
        {
          passive: true
        }
      );

      window.requestAnimationFrame(
        () =>
          centerOn(reals[0])
      );
    };

    const disable = () => {
      if (!clones.length) {
        return;
      }

      viewport.removeEventListener(
        'scroll',
        onScroll
      );

      window.clearTimeout(
        scrollTimer
      );

      clones.forEach((clone) =>
        clone.remove()
      );

      clones = [];
    };

    const apply = () => {
      if (mq.matches) {
        enable();
      } else {
        disable();
      }
    };

    apply();

    mq.addEventListener(
      'change',
      apply
    );
  }

  bindQuantityControls() {
    if (!this.quantityInput) {
      return;
    }

    const decrease =
      this.section.querySelector(
        '[data-quantity-decrease]'
      );

    const increase =
      this.section.querySelector(
        '[data-quantity-increase]'
      );

    decrease?.addEventListener(
      'click',
      () => {
        const nextValue =
          Math.max(
            1,
            Number(
              this.quantityInput.value ||
              1
            ) - 1
          );

        this.quantityInput.value =
          nextValue;
      }
    );

    increase?.addEventListener(
      'click',
      () => {
        const nextValue =
          Math.max(
            1,
            Number(
              this.quantityInput.value ||
              1
            ) + 1
          );

        this.quantityInput.value =
          nextValue;
      }
    );

    this.quantityInput.addEventListener(
      'change',
      () => {
        const normalized =
          Math.max(
            1,
            Number(
              this.quantityInput.value ||
              1
            )
          );

        this.quantityInput.value =
          normalized;
      }
    );
  }

  showMedia(mediaId) {
    const target =
      this.section.querySelector(
        `[data-media-id="${mediaId}"]`
      );

    if (!target) return;

    target.scrollIntoView({
      behavior: 'smooth',
      inline: 'nearest',
      block: 'nearest'
    });

    this.section
      .querySelectorAll(
        '[data-gallery-thumb]'
      )
      .forEach((button) => {
        button.classList.toggle(
          'is-active',
          Number(
            button.dataset.targetMediaId
          ) === mediaId
        );
      });
  }

  bindStickyBar() {
    if (
      !this.stickyBar ||
      !this.addToCart
    ) {
      return;
    }

    const sizePattern =
      /(^|\b)(size|maat|taille|größe|groesse)(\b|$)/i;

    const sizeIndex =
      this.product.options.findIndex(
        (name) =>
          sizePattern.test(name)
      );

    const variantIndex =
      this.product.options.findIndex(
        (_, index) =>
          index !== sizeIndex
      );

    this.stickyOptionIndexes = {
      variant:
        variantIndex >= 0
          ? variantIndex
          : null,

      size:
        sizeIndex >= 0
          ? sizeIndex
          : null
    };

    this.stickySelections = [
      ...(this.currentVariant?.options || [])
    ];

    this.stickyVariant =
      this.currentVariant;

    this.stickyBar.innerHTML = `
      <div class="product-sticky-bar__inner">
        <div
          class="product-sticky-bar__selectors"
          data-sticky-selectors
        ></div>

        <button
          type="button"
          class="product-sticky-bar__submit"
          data-sticky-submit
        >
          ${this.addToCartText}
        </button>
      </div>
    `;

    this.stickySubmit =
      this.stickyBar.querySelector(
        '[data-sticky-submit]'
      );

    this.stickySelectors =
      this.stickyBar.querySelector(
        '[data-sticky-selectors]'
      );

    const createSelector = (
      key,
      optionIndex,
      showSwatch
    ) => {
      if (
        optionIndex === null ||
        optionIndex < 0
      ) {
        return;
      }

      const optionName =
        this.product.options[
          optionIndex
        ];

      const values = [
        ...new Set(
          this.product.variants
            .map(
              (variant) =>
                variant.options[
                  optionIndex
                ]
            )
            .filter(Boolean)
        )
      ];

      const wrapper =
        document.createElement(
          'div'
        );

      wrapper.className =
        `product-sticky-bar__selector product-sticky-bar__selector--${key}`;

      wrapper.dataset.stickySelector =
        key;

      wrapper.dataset.optionIndex =
        String(optionIndex);

      const trigger =
        document.createElement(
          'button'
        );

      trigger.type = 'button';

      trigger.className =
        'product-sticky-bar__selector-trigger';

      trigger.setAttribute(
        'aria-expanded',
        'false'
      );

      trigger.innerHTML = `
        ${
          showSwatch
            ? '<span class="product-sticky-bar__trigger-swatch" data-sticky-trigger-swatch></span>'
            : ''
        }

        <span class="product-sticky-bar__selector-copy">
          <span class="product-sticky-bar__selector-label">
            ${
              key === 'size'
                ? 'Size'
                : 'Variant'
            }
          </span>

          <span
            class="product-sticky-bar__selector-value"
            data-sticky-selector-value
          ></span>
        </span>

        <svg
          class="product-sticky-bar__chevron"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            d="M5.5 12.5 10 8l4.5 4.5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      `;

      const menu =
        document.createElement(
          'div'
        );

      menu.className =
        'product-sticky-bar__menu';

      menu.hidden = true;

      menu.setAttribute(
        'role',
        'listbox'
      );

      menu.setAttribute(
        'aria-label',
        optionName
      );

      values.forEach((value) => {
        const item =
          document.createElement(
            'button'
          );

        item.type = 'button';

        item.className =
          'product-sticky-bar__menu-item';

        item.dataset.stickyOptionValue =
          value;

        item.dataset.optionIndex =
          String(optionIndex);

        item.setAttribute(
          'role',
          'option'
        );

        if (showSwatch) {
          const swatch =
            document.createElement(
              'span'
            );

          swatch.className =
            'product-sticky-bar__menu-swatch';

          swatch.dataset.stickySwatchValue =
            value;

          item.appendChild(swatch);
        }

        const text =
          document.createElement(
            'span'
          );

        text.className =
          'product-sticky-bar__menu-text';

        text.textContent = value;

        item.appendChild(text);

        item.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (item.disabled) {
              return;
            }

            const nextSelections = [
              ...this.stickySelections
            ];

            nextSelections[
              optionIndex
            ] = value;

            const nextVariant =
              this.resolveVariantFromSelection(
                nextSelections,
                optionIndex
              );

            if (!nextVariant) {
              return;
            }

            /*
             * ALLEEN floating selectie.
             *
             * Dus:
             * - geen scroll
             * - geen gallery switch
             * - geen URL wijziging
             * - geen normale selector wijziging
             */
            this.stickyVariant =
              nextVariant;

            this.stickySelections = [
              ...nextVariant.options
            ];

            this.syncStickyBar(
              nextVariant
            );

            this.closeStickyMenus();
          }
        );

        menu.appendChild(item);
      });

      trigger.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();

          const shouldOpen =
            menu.hidden;

          this.closeStickyMenus();

          menu.hidden =
            !shouldOpen;

          trigger.setAttribute(
            'aria-expanded',
            shouldOpen
              ? 'true'
              : 'false'
          );

          wrapper.classList.toggle(
            'is-open',
            shouldOpen
          );
        }
      );

      wrapper.appendChild(trigger);
      wrapper.appendChild(menu);

      this.stickySelectors.appendChild(
        wrapper
      );
    };

    createSelector(
      'variant',
      this.stickyOptionIndexes.variant,
      true
    );

    createSelector(
      'size',
      this.stickyOptionIndexes.size,
      false
    );

    /*
     * BELANGRIJK:
     *
     * We gebruiken GEEN fetch('/cart/add.js')
     * en GEEN redirect naar /cart.
     *
     * We zetten alleen de gekozen floating variant
     * in het echte Shopify productformulier en klikken
     * vervolgens op de ECHTE Add to Cart button.
     *
     * Daardoor krijgt UpCart exact dezelfde flow
     * als bij de normale Add to Cart button.
     */
    this.stickySubmit.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();

        const variant =
          this.stickyVariant ||
          this.currentVariant;

        if (
          !variant ||
          !variant.available
        ) {
          return;
        }

        /*
         * Zet de juiste variant-ID in het echte formulier.
         *
         * GEEN change/input events dispatchen.
         * Daardoor verandert de pagina zelf niet.
         */
        this.variantIdInputs.forEach(
          (input) => {
            input.value =
              variant.id;
          }
        );

        const formVariantInput =
          this.form.querySelector(
            '[name="id"]'
          );

        if (formVariantInput) {
          formVariantInput.value =
            variant.id;
        }

        /*
         * Gebruik de originele ATC button.
         * UpCart kan deze normale Shopify submit onderscheppen.
         */
        const wasDisabled =
          this.addToCart.disabled;

        this.addToCart.disabled =
          false;

        this.addToCart.click();

        /*
         * Herstel daarna de toestand van de normale productpagina.
         * Dit gebeurt pas nádat de click/submit al is afgevuurd.
         */
        window.setTimeout(() => {
          const normalVariant =
            this.currentVariant;

          if (!normalVariant) {
            return;
          }

          this.variantIdInputs.forEach(
            (input) => {
              input.value =
                normalVariant.id;
            }
          );

          const normalFormInput =
            this.form.querySelector(
              '[name="id"]'
            );

          if (normalFormInput) {
            normalFormInput.value =
              normalVariant.id;
          }

          this.addToCart.disabled =
            !normalVariant.available;
        }, 1000);
      }
    );

    this._stickyOutsideHandler =
      (event) => {
        if (
          !this.stickyBar.contains(
            event.target
          )
        ) {
          this.closeStickyMenus();
        }
      };

    this._stickyEscapeHandler =
      (event) => {
        if (event.key === 'Escape') {
          this.closeStickyMenus();
        }
      };

    document.addEventListener(
      'click',
      this._stickyOutsideHandler
    );

    document.addEventListener(
      'keydown',
      this._stickyEscapeHandler
    );

    let hasSeenAddToCart = false;
    let rafId = null;

    const updateStickyBarVisibility =
      () => {
        const buttonBounds =
          this.addToCart.getBoundingClientRect();

        const viewportHeight =
          window.innerHeight ||
          document.documentElement.clientHeight;

        const isVisible =
          buttonBounds.bottom > 0 &&
          buttonBounds.top <
            viewportHeight;

        if (isVisible) {
          hasSeenAddToCart = true;
        }

        const hasScrolledPastButton =
          buttonBounds.bottom <= 0;

        this.stickyBar.hidden =
          !(
            hasSeenAddToCart &&
            hasScrolledPastButton
          );

        rafId = null;
      };

    const requestStickyBarUpdate =
      () => {
        if (rafId !== null) {
          return;
        }

        rafId =
          window.requestAnimationFrame(
            updateStickyBarVisibility
          );
      };

    this.syncStickyBar(
      this.stickyVariant
    );

    updateStickyBarVisibility();

    window.addEventListener(
      'scroll',
      requestStickyBarUpdate,
      {
        passive: true
      }
    );

    window.addEventListener(
      'resize',
      requestStickyBarUpdate
    );
  }

  closeStickyMenus() {
    if (!this.stickyBar) {
      return;
    }

    this.stickyBar
      .querySelectorAll(
        '[data-sticky-selector]'
      )
      .forEach((wrapper) => {
        wrapper.classList.remove(
          'is-open'
        );

        const trigger =
          wrapper.querySelector(
            '.product-sticky-bar__selector-trigger'
          );

        const menu =
          wrapper.querySelector(
            '.product-sticky-bar__menu'
          );

        if (trigger) {
          trigger.setAttribute(
            'aria-expanded',
            'false'
          );
        }

        if (menu) {
          menu.hidden = true;
        }
      });
  }

  getStickyOptionImage(
    optionIndex,
    optionValue
  ) {
    const matchingVariant =
      this.product.variants.find(
        (variant) =>
          variant.options[
            optionIndex
          ] ===
            optionValue &&
          variant.available
      ) ||
      this.product.variants.find(
        (variant) =>
          variant.options[
            optionIndex
          ] === optionValue
      );

    if (!matchingVariant) {
      return '';
    }

    return (
      matchingVariant
        .featured_media
        ?.preview_image
        ?.src ||

      matchingVariant
        .featured_image
        ?.src ||

      matchingVariant
        .featured_image ||

      ''
    );
  }

  paintStickySwatch(
    node,
    optionIndex,
    optionValue
  ) {
    if (!node) return;

    const image =
      this.getStickyOptionImage(
        optionIndex,
        optionValue
      );

    node.style.backgroundImage = '';
    node.style.backgroundColor = '';

    node.classList.remove(
      'has-image'
    );

    if (image) {
      node.style.backgroundImage =
        `url("${String(image).replace(
          /"/g,
          '\\"'
        )}")`;

      node.classList.add(
        'has-image'
      );

      return;
    }

    const cleanedColor =
      String(optionValue || '')
        .trim()
        .replace(/\s+/g, '');

    if (
      window.CSS?.supports?.(
        'color',
        cleanedColor
      )
    ) {
      node.style.backgroundColor =
        cleanedColor;
    } else {
      node.style.backgroundColor =
        '#f3f3f3';
    }
  }

  syncStickyBar(variant) {
    if (
      !this.stickyBar ||
      !variant
    ) {
      return;
    }

    const selectedOptions = [
      ...variant.options
    ];

    this.stickyBar
      .querySelectorAll(
        '[data-sticky-selector]'
      )
      .forEach((wrapper) => {
        const optionIndex =
          Number(
            wrapper.dataset.optionIndex
          );

        const selectedValue =
          selectedOptions[
            optionIndex
          ];

        const valueNode =
          wrapper.querySelector(
            '[data-sticky-selector-value]'
          );

        const triggerSwatch =
          wrapper.querySelector(
            '[data-sticky-trigger-swatch]'
          );

        if (valueNode) {
          valueNode.textContent =
            selectedValue || '';
        }

        if (triggerSwatch) {
          this.paintStickySwatch(
            triggerSwatch,
            optionIndex,
            selectedValue
          );
        }

        wrapper
          .querySelectorAll(
            '[data-sticky-option-value]'
          )
          .forEach((item) => {
            const value =
              item.dataset
                .stickyOptionValue;

            const isActive =
              value === selectedValue;

            const isReachable =
              this.isOptionValueReachable(
                optionIndex,
                value,
                selectedOptions
              );

            item.classList.toggle(
              'is-active',
              isActive
            );

            item.disabled =
              !isReachable;

            item.setAttribute(
              'aria-selected',
              isActive
                ? 'true'
                : 'false'
            );

            item.setAttribute(
              'aria-disabled',
              isReachable
                ? 'false'
                : 'true'
            );

            const swatch =
              item.querySelector(
                '[data-sticky-swatch-value]'
              );

            if (swatch) {
              this.paintStickySwatch(
                swatch,
                optionIndex,
                value
              );
            }
          });
      });

    if (this.stickySubmit) {
      this.stickySubmit.disabled =
        !variant.available;

      this.stickySubmit.textContent =
        variant.available
          ? this.addToCartText
          : this.soldOutText;
    }
  }

  syncStickyFromMainVariant(variant) {
    if (
      !this.stickyBar ||
      !variant
    ) {
      return;
    }

    this.stickyVariant =
      variant;

    this.stickySelections = [
      ...variant.options
    ];

    this.syncStickyBar(variant);
  }

  bindAccordions() {
    if (
      !this.accordionTriggers.length
    ) {
      return;
    }

    this.accordionTriggers.forEach(
      (trigger) => {
        const panelId =
          trigger.getAttribute(
            'aria-controls'
          );

        const panel =
          panelId
            ? this.section.querySelector(
                `#${CSS.escape(panelId)}`
              )
            : null;

        if (!panel) {
          return;
        }

        if (
          panel.dataset
            .accordionOpenDefault ===
          'true'
        ) {
          panel.style.height =
            `${panel.scrollHeight}px`;
        }

        trigger.addEventListener(
          'click',
          () => {
            const isOpen =
              trigger.getAttribute(
                'aria-expanded'
              ) === 'true';

            this.setAccordionState(
              trigger,
              panel,
              !isOpen
            );
          }
        );
      }
    );
  }

  setAccordionState(
    trigger,
    panel,
    shouldOpen
  ) {
    trigger.setAttribute(
      'aria-expanded',
      shouldOpen
        ? 'true'
        : 'false'
    );

    trigger.classList.toggle(
      'is-open',
      shouldOpen
    );

    panel.classList.toggle(
      'is-open',
      shouldOpen
    );

    if (shouldOpen) {
      panel.style.height =
        `${panel.scrollHeight}px`;
    } else {
      panel.style.height =
        `${panel.scrollHeight}px`;

      requestAnimationFrame(() => {
        panel.style.height =
          '0px';
      });
    }
  }

formatMoney(amount) {
  const value = Number(amount || 0) / 100;

  const formatted = new Intl.NumberFormat('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);

  return `$${formatted}`;
}
}

document
  .querySelectorAll(
    '[data-product-section]'
  )
  .forEach((section) => {
    new ProductSectionController(
      section
    );
  });